import type { SimulationWrapperType } from "@/test-set/simulation-wrappers"

import { TRACKING_EVENTS, type TrackingEventName } from "@/lib/analytics"
import type {
  TestCaseExpectedOutput,
  TestCaseSimulationMetadataInput,
} from "@/types/backend-contracts"
import type { ScriptTurn } from "@/types/ScriptTurn"
import {
  ALLOWED_MEDIA_MIME_TYPES,
  MAX_MEDIA_FILE_SIZE_BYTES,
  type MediaAttachment,
} from "@/types/TestCaseInput"
import { TestSetType } from "@/types/TestSetInput"

import type { LocalMediaAttachment } from "../components/MediaAttachmentSection"
import type {
  EditableTestCase,
  EditableTestCaseRecordLike,
  HydratedEditableTestCase,
  TestSetMetadata,
} from "../model"
import { isTempClientId } from "./tempClientId"

const MAX_MEDIA_FILE_SIZE_MB = MAX_MEDIA_FILE_SIZE_BYTES / (1024 * 1024)
const DEFAULT_TEST_SET_NAME = "New Test Set"

interface SaveableTestCaseInput {
  id: string
  input_str: string
  expected_behaviors: string[]
  expected_output_json?: TestCaseExpectedOutput | null
  s3_key?: string | null
  s3_bucket?: string | null
  input_type: string
  script_turns?: ScriptTurn[] | null
  simulation_metadata_input?: TestCaseSimulationMetadataInput | null
  description?: string
  connected_knowledge_base_entries?: EditableTestCase["connected_knowledge_base_entries"]
  media_attachments?: MediaAttachment[]
}

interface SaveMetadata {
  displayName: string
  description: string
  simulationWrapperType: SimulationWrapperType | null
}

interface MediaUploadRequest {
  testCaseId: string
  attachment: LocalMediaAttachment
}

interface DeferredMediaUpload {
  tempTestCaseId: string
  attachment: LocalMediaAttachment
}

interface UploadedMediaAttachment {
  testCaseId: string
  attachment: MediaAttachment
}

interface MediaUploadPlan {
  baseTestCases: HydratedEditableTestCase[]
  immediateUploads: MediaUploadRequest[]
  deferredUploads: DeferredMediaUpload[]
}

interface Identifiable {
  id: string
}

type DiscardBehavior =
  | {
      kind: "navigate"
      href: string
      trackingEvent: TrackingEventName
      trackingProperties: {
        test_case_count: number
      }
    }
  | {
      kind: "delete"
      href: string
      testSetId: string
      trackingEvent: TrackingEventName
      trackingProperties: {
        test_set_id: string
      }
    }

function normalizeMediaAttachments(
  mediaAttachments?: MediaAttachment[] | null,
): MediaAttachment[] {
  return Array.isArray(mediaAttachments) ? mediaAttachments : []
}

function normalizeInputType(inputType: string): string {
  switch (inputType) {
    case "DEFAULT":
    case "STREAM":
    case "TRANSCRIPT":
    case "SCENARIO":
    case "WORKFLOW":
    case "AUDIO":
    case "IVR":
    case "SCRIPT":
      return inputType
    default:
      return "SCENARIO"
  }
}

export function buildSaveMetadata(metadata: TestSetMetadata): SaveMetadata {
  const displayName = (metadata.displayName || "").trim()

  return {
    displayName: displayName || DEFAULT_TEST_SET_NAME,
    description: metadata.description || "",
    simulationWrapperType: metadata.simulationWrapperType || null,
  }
}

export function buildCreateTestSetInput({
  metadata,
  testCases,
  includeTestCases = true,
}: {
  metadata: TestSetMetadata
  testCases: EditableTestCase[]
  includeTestCases?: boolean
}) {
  const saveMetadata = buildSaveMetadata(metadata)

  return {
    display_name: saveMetadata.displayName,
    description: saveMetadata.description,
    test_set_type: TestSetType.DEFAULT,
    testCases: includeTestCases
      ? testCases.map((testCase) => {
          const normalizedType = normalizeInputType(testCase.input_type)
          const isScript = normalizedType === "SCRIPT"

          return {
            input_str: isScript ? "" : testCase.input_str,
            expected_behaviors: testCase.expected_behaviors,
            expected_output_json: testCase.expected_output_json ?? null,
            s3_key: testCase.s3_key,
            s3_bucket: testCase.s3_bucket,
            input_type: normalizedType,
            script_turns: isScript ? (testCase.script_turns ?? null) : null,
            simulation_metadata_input:
              testCase.simulation_metadata_input ?? null,
            description: testCase.description ?? undefined,
            connected_knowledge_base_entries:
              testCase.connected_knowledge_base_entries ?? [],
          }
        })
      : [],
    parameters: {} as Record<string, string[]>,
  }
}

export function toSaveEditorStateTestCase(
  testCase: HydratedEditableTestCase,
): SaveableTestCaseInput {
  const isScript = testCase.input_type === "SCRIPT"

  return {
    id: testCase.id,
    input_str: isScript ? "" : testCase.input_str,
    expected_behaviors: testCase.expected_behaviors,
    expected_output_json: testCase.expected_output_json ?? null,
    s3_key: testCase.s3_key,
    s3_bucket: testCase.s3_bucket,
    input_type: normalizeInputType(testCase.input_type),
    script_turns: isScript ? (testCase.script_turns ?? null) : null,
    simulation_metadata_input: testCase.simulation_metadata_input ?? null,
    description: testCase.description ?? undefined,
    connected_knowledge_base_entries:
      testCase.connected_knowledge_base_entries ?? [],
    media_attachments: normalizeMediaAttachments(testCase.media_attachments),
  }
}

export function createMediaUploadPlan({
  testCases,
  localMediaAttachmentsByTestCaseId,
}: {
  testCases: EditableTestCase[]
  localMediaAttachmentsByTestCaseId: Record<string, LocalMediaAttachment>
}): MediaUploadPlan {
  const immediateUploads: MediaUploadRequest[] = []
  const deferredUploads: DeferredMediaUpload[] = []

  const baseTestCases = testCases.map((testCase) => {
    const localAttachment = localMediaAttachmentsByTestCaseId[testCase.id]
    if (!localAttachment) {
      return {
        ...testCase,
        media_attachments: normalizeMediaAttachments(
          testCase.media_attachments,
        ),
      }
    }

    if (isTempClientId(testCase.id)) {
      deferredUploads.push({
        tempTestCaseId: testCase.id,
        attachment: localAttachment,
      })
    } else {
      immediateUploads.push({
        testCaseId: testCase.id,
        attachment: localAttachment,
      })
    }

    return {
      ...testCase,
      media_attachments: [],
    }
  })

  return {
    baseTestCases,
    immediateUploads,
    deferredUploads,
  }
}

export function remapPersistedTestCaseIds<T extends Identifiable>(
  testCases: T[],
  createdIdMap: Record<string, string>,
): T[] {
  if (Object.keys(createdIdMap).length === 0) {
    return testCases
  }

  return testCases.map((testCase) => ({
    ...testCase,
    id: createdIdMap[testCase.id] ?? testCase.id,
  }))
}

export function remapLocalMediaAttachmentsByTestCaseId({
  localMediaAttachmentsByTestCaseId,
  createdIdMap,
}: {
  localMediaAttachmentsByTestCaseId: Record<string, LocalMediaAttachment>
  createdIdMap: Record<string, string>
}): Record<string, LocalMediaAttachment> {
  if (Object.keys(createdIdMap).length === 0) {
    return localMediaAttachmentsByTestCaseId
  }

  return Object.entries(localMediaAttachmentsByTestCaseId).reduce<
    Record<string, LocalMediaAttachment>
  >((attachmentsByTestCaseId, [testCaseId, attachment]) => {
    attachmentsByTestCaseId[createdIdMap[testCaseId] ?? testCaseId] = attachment

    return attachmentsByTestCaseId
  }, {})
}

export function applyUploadedMediaAttachments({
  testCases,
  uploadedAttachments,
}: {
  testCases: HydratedEditableTestCase[]
  uploadedAttachments: UploadedMediaAttachment[]
}): HydratedEditableTestCase[] {
  const uploadedByTestCaseId = new Map(
    uploadedAttachments.map(({ testCaseId, attachment }) => [
      testCaseId,
      attachment,
    ]),
  )

  return testCases.map((testCase) => {
    const uploadedAttachment = uploadedByTestCaseId.get(testCase.id)
    if (!uploadedAttachment) {
      return {
        ...testCase,
        media_attachments: normalizeMediaAttachments(
          testCase.media_attachments,
        ),
      }
    }

    return {
      ...testCase,
      media_attachments: [uploadedAttachment],
    }
  })
}

export function resolveDeferredMediaUploads({
  createdIdMap,
  deferredUploads,
}: {
  createdIdMap: Record<string, string>
  deferredUploads: DeferredMediaUpload[]
}): MediaUploadRequest[] {
  return deferredUploads.map((pendingUpload) => {
    const persistedTestCaseId = createdIdMap[pendingUpload.tempTestCaseId]
    if (!persistedTestCaseId) {
      throw new Error(
        `Failed to resolve saved test case id for ${pendingUpload.tempTestCaseId}`,
      )
    }

    return {
      testCaseId: persistedTestCaseId,
      attachment: pendingUpload.attachment,
    }
  })
}

export function hydrateSavedTestCases(
  testCases: EditableTestCaseRecordLike[],
): HydratedEditableTestCase[] {
  return testCases.map((testCase) => ({
    id: testCase.id,
    input_str: testCase.input_str || "",
    expected_behaviors: testCase.expected_behaviors || [],
    expected_output_json: testCase.expected_output_json || null,
    s3_key: testCase.s3_key || null,
    s3_bucket: testCase.s3_bucket || null,
    input_type: normalizeInputType(testCase.input_type ?? "SCENARIO"),
    script_turns: testCase.script_turns ?? null,
    simulation_metadata_input: testCase.simulation_metadata_input ?? null,
    description: testCase.description ?? undefined,
    connected_knowledge_base_entries:
      testCase.connected_knowledge_base_entries ?? [],
    media_attachments: normalizeMediaAttachments(testCase.media_attachments),
  }))
}

export function resolveNextActiveAgentId({
  activeAgentId,
  selectedAgentIds,
}: {
  activeAgentId: string
  selectedAgentIds: string[]
}): string {
  return selectedAgentIds.includes(activeAgentId)
    ? activeAgentId
    : (selectedAgentIds[0] ?? "")
}

export function validateLocalMediaAttachment(
  attachment: LocalMediaAttachment,
): void {
  if (!attachment.file?.type) {
    throw new Error("Selected media file is missing MIME type.")
  }

  if (!isAllowedMediaMimeType(attachment.file.type)) {
    throw new Error(`Unsupported media file type: ${attachment.file.type}`)
  }

  if (attachment.file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
    throw new Error(`Selected media file exceeds ${MAX_MEDIA_FILE_SIZE_MB} MB.`)
  }
}

export function buildUploadedMediaAttachment({
  attachment,
  s3Bucket,
  s3Key,
}: {
  attachment: LocalMediaAttachment
  s3Bucket: string
  s3Key: string
}): MediaAttachment {
  const mimeType = getValidatedMediaMimeType(attachment.file.type)

  return {
    name: attachment.name.trim(),
    description: attachment.description?.trim() || undefined,
    mime_type: mimeType,
    s3_key: s3Key,
    s3_bucket: s3Bucket,
  }
}

export function getDiscardBehavior({
  isUnsavedNew,
  listUrl,
  testCaseCount,
  testSetId,
}: {
  isUnsavedNew: boolean
  listUrl: string
  testCaseCount: number
  testSetId?: string
}): DiscardBehavior {
  if (isUnsavedNew) {
    return {
      kind: "navigate",
      href: listUrl,
      trackingEvent: TRACKING_EVENTS.TEST_SET.DISCARDED,
      trackingProperties: {
        test_case_count: testCaseCount,
      },
    }
  }

  if (!testSetId) {
    throw new Error("Failed to resolve test set id for discard.")
  }

  return {
    kind: "delete",
    href: listUrl,
    testSetId,
    trackingEvent: TRACKING_EVENTS.TEST_SET.DELETED,
    trackingProperties: {
      test_set_id: testSetId,
    },
  }
}

function isAllowedMediaMimeType(
  mimeType: string,
): mimeType is MediaAttachment["mime_type"] {
  return ALLOWED_MEDIA_MIME_TYPES.some(
    (allowedMimeType) => allowedMimeType === mimeType,
  )
}

function getValidatedMediaMimeType(
  mimeType: string,
): MediaAttachment["mime_type"] {
  if (!isAllowedMediaMimeType(mimeType)) {
    throw new Error(`Unsupported media file type: ${mimeType}`)
  }

  return mimeType
}
